// Lv1: 時刻アナウンス（DB対応版）
import { useEffect, useState } from 'react';
import { Lv1Task } from '../types';
import { loadSettings } from '../lib/storage';
import { speak } from '../lib/speech';
import { Scheduler } from '../lib/scheduler';
import { appendReactionDb } from '../lib/db';
import DowPicker from '../components/DowPicker';
import StatusBanner from '../components/StatusBanner';
import { useTasks } from '../hooks/useTasks';
import { useAuth } from '../contexts/AuthContext';

export default function Lv1Page() {
  const { family } = useAuth();
  const { tasks, add, update, remove } = useTasks('lv1');
  const [time, setTime] = useState('07:00');
  const [message, setMessage] = useState('おはよう！起きる時間だよ');
  const [dow, setDow] = useState<number[]>([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!family) return;
    const scheduler = new Scheduler(
      () => (tasks as Lv1Task[]),
      async ({ task }) => {
        if (task.level !== 'lv1') return;
        const settings = loadSettings();
        const lv1 = task as Lv1Task;
        try {
          await speak(lv1.message, settings);
          await appendReactionDb(
            {
              taskId: task.id,
              taskTitle: lv1.message,
              level: 'lv1',
              firedAt: Date.now(),
              announcement: lv1.message,
            },
            family.id
          );
        } catch (err) {
          console.error('[Lv1] 読み上げ失敗', err);
        }
      }
    );
    scheduler.start();
    setRunning(true);
    return () => {
      scheduler.stop();
      setRunning(false);
    };
  }, [family, tasks]);

  const handleAdd = async () => {
    if (!time || !message.trim()) return;
    await add({
      level: 'lv1',
      time,
      message: message.trim(),
      daysOfWeek: dow,
      enabled: true,
    } as Omit<Lv1Task, 'id' | 'createdAt'>);
    setMessage('');
    setDow([]);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('このタスクを削除しますか？')) return;
    await remove(id);
  };

  const handleToggle = async (t: Lv1Task) => {
    await update(t.id, { enabled: !t.enabled });
  };

  const handleTestSpeak = async (msg: string) => {
    try {
      await speak(msg, loadSettings());
    } catch (err) {
      alert(`再生失敗: ${err}`);
    }
  };

  const lv1Tasks = tasks as Lv1Task[];

  return (
    <>
      <StatusBanner running={running} taskCount={lv1Tasks.filter((t) => t.enabled).length} />

      <div className="card">
        <h2>Lv1 タスクを追加</h2>
        <p className="description">指定時刻に1回、メッセージを読み上げます。</p>
        <div className="form-row-inline">
          <div className="form-row" style={{ minWidth: 140 }}>
            <label>時刻</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div className="form-row" style={{ flex: 1, minWidth: 200 }}>
            <label>メッセージ</label>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="例: おやつの時間だよ"
            />
          </div>
        </div>
        <div className="form-row">
          <label>曜日</label>
          <DowPicker value={dow} onChange={setDow} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={handleAdd}>
            追加する
          </button>
          <button className="btn btn-secondary" onClick={() => handleTestSpeak(message)}>
            🔊 試しに今すぐ読み上げ
          </button>
        </div>
      </div>

      <div className="card">
        <h2>登録済みタスク（{lv1Tasks.length}件）</h2>
        {lv1Tasks.length === 0 ? (
          <p className="muted">まだ何も登録されていません。</p>
        ) : (
          <div className="task-list">
            {lv1Tasks.map((t) => (
              <div key={t.id} className="task-item" style={{ opacity: t.enabled ? 1 : 0.5 }}>
                <div className="info">
                  <div className="time">{t.time}</div>
                  <div className="msg">{t.message}</div>
                  <div className="meta">
                    {t.daysOfWeek.length === 0
                      ? '毎日'
                      : t.daysOfWeek.map((d) => '日月火水木金土'[d]).join('・')}
                  </div>
                </div>
                <div className="actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => handleToggle(t)}>
                    {t.enabled ? '停止' : '再開'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t.id)}>
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
